import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './CEPTPage.css';

function CEPTPage({ content, passage, questionType, topic }) {
  const { t, i18n } = useTranslation();
  // All hooks at the top!
  const [mode, setMode] = useState('reading'); // 'reading' or 'practice'
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [markingResults, setMarkingResults] = useState({});
  const [incompleteWarning, setIncompleteWarning] = useState(false);
  const questionRefs = React.useRef({});
  const [isMarking, setIsMarking] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryMessage, setRetryMessage] = useState('');
  const maxRetries = 3;
  const progressBarRef = React.useRef(null);

  React.useEffect(() => {
    setAnsweredCount(Object.keys(userAnswers).filter(q => userAnswers[q] && userAnswers[q].toString().trim() !== '').length);
  }, [userAnswers]);

  if (!content || typeof content !== 'string' || !content.trim()) {
    return (
      <div className="cept-guide">
        <div className="practice-mode-placeholder">
          <h3>{t('errors.noContent', 'No content available')}</h3>
          <p style={{ color: '#666', marginTop: '1em' }}>
            {t('errors.generateNewContent', 'Please generate new content or try again.')}
          </p>
        </div>
      </div>
    );
  }

  // Parse CEPT content to extract questions and answers
  function parseCEPTContent(content) {
    const lines = content.split('\n');
    const sections = {
      readingPassage: '',
      questions: [],
      markingScheme: {}
    };
    
    let currentSection = null;
    let currentQuestion = null;
    let inReadingPassage = false;
    let inQuestions = false;
    let inMarkingScheme = false;
    let inGrammarQuestions = false;
    let inVocabularyQuestions = false;
    let questionContext = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Detect sections
      if (line.includes('Reading Passage')) {
        currentSection = 'readingPassage';
        inReadingPassage = true;
        inQuestions = false;
        inMarkingScheme = false;
        inGrammarQuestions = false;
        inVocabularyQuestions = false;
        questionContext = '';
        continue;
      } else if (line.includes('Grammar Questions:') || line.includes('Questions:')) {
        currentSection = 'questions';
        inReadingPassage = false;
        inQuestions = true;
        inMarkingScheme = false;
        inGrammarQuestions = true;
        inVocabularyQuestions = false;
        questionContext = '';
        continue;
      } else if (line.includes('Vocabulary Questions:')) {
        inGrammarQuestions = false;
        inVocabularyQuestions = true;
        questionContext = '';
        continue;
      } else if (line.includes('Marking Scheme')) {
        currentSection = 'markingScheme';
        inReadingPassage = false;
        inQuestions = false;
        inMarkingScheme = true;
        inGrammarQuestions = false;
        inVocabularyQuestions = false;
        questionContext = '';
        continue;
      }
      
      // Parse content based on current section
      if (inReadingPassage && line && !line.includes('Questions:')) {
        if (!sections.readingPassage) {
          sections.readingPassage = line;
        } else {
          // Add proper paragraph breaks
          if (line.trim() === '') {
            sections.readingPassage += '\n\n';
          } else {
            sections.readingPassage += '\n' + line;
          }
        }
      } else if ((inQuestions || inGrammarQuestions || inVocabularyQuestions) && line && !line.includes('Marking Scheme')) {
        if (line.match(/^\d+\./)) {
          // Save any accumulated context for the previous question
          if (currentQuestion && questionContext) {
            currentQuestion.text = questionContext.trim() + ' ' + currentQuestion.text;
          }
          
          const questionText = line.replace(/^\d+\.\s*/, '');
          let questionType = 'mcq'; // default
          
          // Determine question type based on content and context
          if (questionText.toLowerCase().includes('complete the sentence') || 
              questionText.toLowerCase().includes('sentence completion') ||
              questionText.toLowerCase().includes('fill in the blank') ||
              questionText.toLowerCase().includes('missing word')) {
            questionType = 'sentence-completion';
            // Remove any answer hints from the question text
            const cleanText = questionText.replace(/\s*\(ANSWER FORMAT:.*?\)\s*/gi, '')
                                        .replace(/\s*\(Correct answer:.*?\)\s*/gi, '')
                                        .replace(/\s*\*\*.*?\*\*\s*/g, '');
            currentQuestion = { 
              qNum: line.match(/^(\d+)\./)[1], 
              text: cleanText, 
              options: [],
              type: questionType
            };
          } else if (questionText.toLowerCase().includes('vocabulary') || 
                     questionText.toLowerCase().includes('grammar') ||
                     questionText.toLowerCase().includes('multiple choice')) {
            questionType = 'mcq';
            currentQuestion = { 
              qNum: line.match(/^(\d+)\./)[1], 
              text: questionText, 
              options: [],
              type: questionType
            };
          } else if (questionText.toLowerCase().includes('explain') ||
                     questionText.toLowerCase().includes('describe') ||
                     questionText.toLowerCase().includes('discuss') ||
                     questionText.toLowerCase().includes('in your own words') ||
                     questionText.toLowerCase().includes('sentences') ||
                     questionText.toLowerCase().includes('paragraph')) {
            // Written response questions
            questionType = 'short';
            currentQuestion = { 
              qNum: line.match(/^(\d+)\./)[1], 
              text: questionText, 
              options: [],
              type: questionType
            };
          } else {
            // Default to MCQ, but we'll check for options later
            currentQuestion = { 
              qNum: line.match(/^(\d+)\./)[1], 
              text: questionText, 
              options: [],
              type: 'mcq'
            };
          }
          
          sections.questions.push(currentQuestion);
          questionContext = '';
        } else if (line.match(/^[ABCD]\./) && currentQuestion) {
          // Regular MCQ option
          currentQuestion.options.push(line);
        } else if (line && currentQuestion && !line.match(/^[ABCD]\./)) {
          // This might be additional context for the question
          if (!currentQuestion.options.length) {
            // For questions without options yet, this might be the actual question content
            if (currentQuestion.text && currentQuestion.text.length < 50) {
              // If the question text is short, this line might be the actual question
              currentQuestion.text += ' ' + line;
            } else {
              // Otherwise, accumulate context
              questionContext += ' ' + line;
            }
          }
        } else if (line && !currentQuestion) {
          // This might be context before the first question
          questionContext += ' ' + line;
        }
        
        // Special handling for Question 4 (sentence completion) - ensure we capture the full sentence
        if (currentQuestion && currentQuestion.qNum === '4' && currentQuestion.type === 'sentence-completion') {
          if (line && !line.match(/^[ABCD]\./) && !line.includes('Marking Scheme') && !line.trim().startsWith('**')) {
            // For Question 4, accumulate context but prevent duplication
            if (line.trim()) {
              // Only add if this content isn't already in the question text
              const trimmedLine = line.trim();
              if (!currentQuestion.text.includes(trimmedLine)) {
                questionContext += ' ' + trimmedLine;
              }
            }
          }
        }
      } else if (inMarkingScheme && line) {
        const modelMatch = line.match(/^(\d+)\.\s*(.*)$/);
        if (modelMatch) {
          const qNum = modelMatch[1];
          const answer = modelMatch[2].trim();
          sections.markingScheme[qNum] = answer;
        }
      }
    }
    
    // Add any remaining context to the last question
    if (currentQuestion && questionContext) {
      currentQuestion.text = questionContext.trim() + ' ' + currentQuestion.text;
    }
    
    console.log('=== CEPT CONTENT PARSING DEBUG ===');
    console.log('Parsed sections:', sections);
    console.log('Questions found:', sections.questions.length);
    sections.questions.forEach((q, i) => {
      console.log(`Question ${i + 1}:`, {
        qNum: q.qNum,
        text: q.text,
        type: q.type,
        options: q.options
      });
    });
    
    return sections;
  }

  // Parse marking scheme
  function parseCEPTMarkingScheme(content) {
    console.log('=== PARSING MARKING SCHEME ===');
    console.log('Content to parse:', content);
    
    const markingSchemeMatch = content.match(/(Marking Scheme[\s\S]*)/i);
    if (!markingSchemeMatch) {
      console.log('No marking scheme found in content');
      return {};
    }
    
    const markingScheme = markingSchemeMatch[1];
    console.log('Found marking scheme:', markingScheme);
    
    const lines = markingScheme.split('\n');
    const answers = {};
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      console.log(`Line ${index}: "${trimmedLine}"`);
      
      // Try multiple patterns to match question answers
      let modelMatch = null;
      
      // Pattern 1: "Model Answer: 1. B" or "Question 1: B"
      modelMatch = trimmedLine.match(/^(?:Model\s*Answer|Question|Q)?\s*(\d+)[.:)?]?\s*(.*)$/i);
      
      // Pattern 2: "1. B" or "1) B"
      if (!modelMatch) {
        modelMatch = trimmedLine.match(/^(\d+)[.:)?]\s*(.*)$/i);
      }
      
      // Pattern 3: "Answer 1: B" or "A1: B"
      if (!modelMatch) {
        modelMatch = trimmedLine.match(/^(?:Answer\s*)?(\d+)[.:)?]\s*(.*)$/i);
      }
      
      if (modelMatch) {
        const qNum = modelMatch[1];
        let modelAnswerText = modelMatch[2].trim();
        modelAnswerText = modelAnswerText.replace(/\s*\(\d+\s*marks?\)\s*$/i, '').trim();
        
        console.log(`Found answer for question ${qNum}: "${modelAnswerText}"`);
        
        if (!answers[qNum]) answers[qNum] = {};
        answers[qNum].modelAnswer = modelAnswerText;
      }
    });
    
    console.log('Final parsed answers:', answers);
    return answers;
  }

  // Parse content
  const ceptContent = parseCEPTContent(content);
  const answers = parseCEPTMarkingScheme(content);

  // Convert CEPT content to questions format for consistency
  const questions = [];

  // Add Questions from the simplified format (4 questions total)
  ceptContent.questions.forEach((question, idx) => {
    // Reclassify questions without MCQ options as short answer questions
    let finalType = question.type;
    if (question.type === 'mcq' && (!question.options || question.options.length === 0)) {
      finalType = 'short';
    }
    
    questions.push({
      qNum: (idx + 1).toString(),
      qText: question.text,
      type: finalType,
      options: question.options.map((opt, optIdx) => ({
        label: String.fromCharCode(65 + optIdx),
        text: opt.replace(/^[ABCD]\.\s*/, '')
      }))
    });
  });

  // Debug logging for marking scheme
  console.log('=== MARKING SCHEME DEBUG ===');
  console.log('Raw content:', content);
  console.log('Parsed answers:', answers);
  console.log('Questions that need marking:', questions.map(q => ({ qNum: q.qNum, type: q.type, hasModelAnswer: !!answers[q.qNum]?.modelAnswer })));

  // Debug logging
  console.log('Raw CEPT content:', content);
  console.log('Parsed CEPT content:', ceptContent);
  console.log('Parsed questions:', ceptContent.questions);
  console.log('Final questions array:', questions);
  
  // Debug the raw content lines to see what we're parsing
  console.log('Raw content lines:', content.split('\n').map((line, idx) => `${idx}: "${line.trim()}"`));
  
  // Add Multiple Choice Gap Fill questions (if any were in the original content)
  // This part of the parsing logic needs to be re-evaluated based on the new simplified format
  // For now, we'll assume any remaining questions are MCQs or short answers if they were in the original content
  // and not already added as questions.
  // The original parsing logic for multipleChoiceGapFill and openGapFill is removed.

  // Add Extended Reading questions (if any were in the original content)
  // This part of the parsing logic needs to be re-evaluated based on the new simplified format
  // For now, we'll assume any remaining questions are MCQs or short answers if they were in the original content
  // and not already added as questions.

  // Function to handle practice submission
  async function handlePracticeSubmit(e) {
    e.preventDefault();
    
    // Check if all questions are answered
    const allAnswered = questions.every(q => {
      if (q.type === 'sentence-completion') {
        // For sentence completion, check if it has multiple blanks
        const sentence = q.qText;
        const blankMatches = sentence.match(/_{3,}/g);
        
        if (blankMatches && blankMatches.length > 1) {
          // Multiple blanks - check each blank
          return blankMatches.every((blank, index) => {
            const blankKey = `${q.qNum}_blank_${index}`;
            return userAnswers[blankKey] && userAnswers[blankKey].toString().trim() !== '';
          });
        } else {
          // Single blank
          return userAnswers[q.qNum] && userAnswers[q.qNum].toString().trim() !== '';
        }
      } else {
        // Other question types
        return userAnswers[q.qNum] && userAnswers[q.qNum].toString().trim() !== '';
      }
    });

    if (!allAnswered) {
      setIncompleteWarning(true);
      return;
    }

    setIncompleteWarning(false);
    setIsMarking(true);
    setRetryCount(0);
    setRetryMessage('');
    setShowResults(false);

    // Prepare answers for submission
    const submissionAnswers = {};
    questions.forEach(q => {
      if (q.type === 'sentence-completion') {
        // For sentence completion, check if it has multiple blanks
        const sentence = q.qText;
        const blankMatches = sentence.match(/_{3,}/g);
        
        if (blankMatches && blankMatches.length > 1) {
          // Multiple blanks - combine answers
          const blankAnswers = [];
          blankMatches.forEach((blank, index) => {
            const blankKey = `${q.qNum}_blank_${index}`;
            blankAnswers.push(userAnswers[blankKey] || '');
          });
          submissionAnswers[q.qNum] = blankAnswers.join(' | ');
        } else {
          // Single blank
          submissionAnswers[q.qNum] = userAnswers[q.qNum] || '';
        }
      } else {
        // Other question types
        submissionAnswers[q.qNum] = userAnswers[q.qNum] || '';
      }
    });

    // Start the retry process
    await attemptMarking(submissionAnswers);
  }

  async function attemptMarking(submissionAnswers) {
    try {
      // Use optimized approach: pre-generated answers for MCQs, AI for text input
      const newMarkingResults = {};
      
      for (const q of questions) {
        const userAnswer = submissionAnswers[q.qNum] || '';
        if (userAnswer.trim() === '') continue;
        
        try {
          if (q.type === 'mcq') {
            // For MCQ questions, use pre-generated answers for instant marking
            await markWithPreGeneratedAnswer(q, userAnswer, newMarkingResults);
          } else {
            // For text input questions (short answer, sentence completion), try AI first
            try {
              await markWithAI(q, userAnswer, newMarkingResults);
            } catch (aiError) {
              console.log(`AI marking failed for question ${q.qNum}, trying pre-generated answer fallback`);
              // If AI marking fails, try to use pre-generated answer as fallback
              await markWithPreGeneratedAnswer(q, userAnswer, newMarkingResults);
            }
          }
        } catch (err) {
          console.error(`Error marking question ${q.qNum}:`, err);
          // Provide fallback marking
          newMarkingResults[q.qNum] = `Correct: No\nFeedback: Unable to mark this question at the moment. Please try again.\nModel Answer: No model answer available.`;
        }
      }
      
      setMarkingResults(newMarkingResults);
      setShowResults(true);
      setRetryMessage(''); // Clear any retry messages
      
    } catch (error) {
      console.error('Error marking answers:', error);
      handleMarkingError(error);
    }
    
    setIsMarking(false);
  }

  // Function to mark MCQs using pre-generated answers
  async function markWithPreGeneratedAnswer(q, userAnswer, newMarkingResults) {
    const modelAnswer = answers[q.qNum]?.modelAnswer;
    
    if (modelAnswer) {
      console.log(`Marking with pre-generated answer for question ${q.qNum}:`, {
        userAnswer,
        modelAnswer,
        questionType: q.type,
        options: q.options
      });
      
      if (q.type === 'mcq') {
        // MCQ marking logic
        // Try multiple patterns to extract the correct answer
        let correctLetter = null;
        
        // Pattern 1: "Correct answer: B" or "Answer: B"
        const pattern1 = modelAnswer.match(/(?:Correct answer:|Answer:)\s*([ABCD])/i);
        if (pattern1) {
          correctLetter = pattern1[1].toUpperCase();
        }
        
        // Pattern 2: "**B**" or "B" at the beginning
        if (!correctLetter) {
          const pattern2 = modelAnswer.match(/^[^A-Z]*([ABCD])/i);
          if (pattern2) {
            correctLetter = pattern2[1].toUpperCase();
          }
        }
        
        // Pattern 3: Look for "**B**" anywhere in the text
        if (!correctLetter) {
          const pattern3 = modelAnswer.match(/\*\*([ABCD])\*\*/i);
          if (pattern3) {
            correctLetter = pattern3[1].toUpperCase();
          }
        }
        
        // Pattern 4: Look for "B -" or "B:" format
        if (!correctLetter) {
          const pattern4 = modelAnswer.match(/([ABCD])\s*[-:]/i);
          if (pattern4) {
            correctLetter = pattern4[1].toUpperCase();
          }
        }
        
        // Pattern 5: Look for "B (got)" or similar
        if (!correctLetter) {
          const pattern5 = modelAnswer.match(/([ABCD])\s*\(/i);
          if (pattern5) {
            correctLetter = pattern5[1].toUpperCase();
          }
        }
        
        // Pattern 6: If the model answer is just a single letter
        if (!correctLetter && /^[ABCD]$/i.test(modelAnswer.trim())) {
          correctLetter = modelAnswer.trim().toUpperCase();
        }
        
        console.log(`Extracted correct letter: ${correctLetter}`);
        
        if (correctLetter) {
          const userLetter = userAnswer.toUpperCase();
          const isCorrect = correctLetter === userLetter;
          
          console.log(`Comparison: User=${userLetter}, Correct=${correctLetter}, IsCorrect=${isCorrect}`);
          
          // Enhanced explanation extraction
          let explanation = `The correct answer is ${correctLetter}.`;
          
          // Try multiple patterns to extract detailed explanation
          let detailedExplanation = null;
          
          // Pattern 1: "Correct answer: B - explanation" or "Answer: B: explanation"
          const explanationPattern1 = modelAnswer.match(/(?:Correct answer:|Answer:)\s*[ABCD]\s*[-:]\s*(.*)/i);
          if (explanationPattern1 && explanationPattern1[1].trim()) {
            detailedExplanation = explanationPattern1[1].trim();
          }
          
          // Pattern 2: "B - explanation" or "B: explanation"
          if (!detailedExplanation) {
            const explanationPattern2 = modelAnswer.match(/[ABCD]\s*[-:]\s*(.*)/i);
            if (explanationPattern2 && explanationPattern2[1].trim()) {
              detailedExplanation = explanationPattern2[1].trim();
            }
          }
          
          // Pattern 3: "**B** - explanation" or "**B**: explanation"
          if (!detailedExplanation) {
            const explanationPattern3 = modelAnswer.match(/\*\*[ABCD]\*\*\s*[-:]\s*(.*)/i);
            if (explanationPattern3 && explanationPattern3[1].trim()) {
              detailedExplanation = explanationPattern3[1].trim();
            }
          }
          
          // Pattern 4: Look for explanation after parentheses like "B (explanation)"
          if (!detailedExplanation) {
            const explanationPattern4 = modelAnswer.match(/[ABCD]\s*\((.*?)\)/i);
            if (explanationPattern4 && explanationPattern4[1].trim()) {
              detailedExplanation = explanationPattern4[1].trim();
            }
          }
          
          // Pattern 5: If the model answer contains more than just the letter, use the rest as explanation
          if (!detailedExplanation && modelAnswer.length > 5) {
            // Remove the letter and common prefixes, use the rest
            const cleanAnswer = modelAnswer.replace(/^(?:Correct answer:|Answer:)?\s*\*\*?[ABCD]\*\*?\s*[-:]?\s*/i, '').trim();
            if (cleanAnswer && cleanAnswer.length > 10) {
              detailedExplanation = cleanAnswer;
            }
          }
          
          // Use the detailed explanation if found, otherwise use the basic one
          if (detailedExplanation) {
            explanation = detailedExplanation;
          }
          
          // Create enhanced feedback
          let feedback;
          if (isCorrect) {
            feedback = `Excellent! You selected the correct answer. ${explanation}`;
          } else {
            feedback = `Incorrect. ${explanation}`;
          }
          
          newMarkingResults[q.qNum] = `Correct: ${isCorrect ? 'Yes' : 'No'}\nFeedback: ${feedback}\nModel Answer: ${correctLetter}`;
        } else {
          // Fallback: direct comparison
          const isCorrect = userAnswer.toLowerCase().trim() === modelAnswer.toLowerCase().trim();
          newMarkingResults[q.qNum] = `Correct: ${isCorrect ? 'Yes' : 'No'}\nFeedback: ${isCorrect ? 'Correct answer!' : 'Try again.'}\nModel Answer: ${modelAnswer}`;
        }
      } else {
        // Short answer or sentence completion marking logic
        console.log(`Marking short answer question ${q.qNum} with pre-generated answer`);
        
        // For short answer questions, do keyword matching
        const userWords = userAnswer.toLowerCase().split(/\s+/).filter(word => word.length > 2);
        const modelWords = modelAnswer.toLowerCase().split(/\s+/).filter(word => word.length > 2);
        
        // Find common words
        const commonWords = userWords.filter(word => modelWords.includes(word));
        const matchPercentage = commonWords.length / Math.max(userWords.length, modelWords.length);
        
        console.log(`Keyword matching for question ${q.qNum}:`, {
          userWords,
          modelWords,
          commonWords,
          matchPercentage
        });
        
        // Consider it correct if there's a reasonable keyword match
        const isCorrect = matchPercentage >= 0.3; // 30% keyword match threshold
        
        let feedback;
        if (isCorrect) {
          feedback = `Good answer! Your response contains relevant keywords that match the expected answer.`;
        } else {
          feedback = `Your answer doesn't match the expected keywords. Consider reviewing the passage for relevant information.`;
        }
        
        newMarkingResults[q.qNum] = `Correct: ${isCorrect ? 'Yes' : 'No'}\nFeedback: ${feedback}\nModel Answer: ${modelAnswer}`;
      }
    } else {
      // No pre-generated answer available
      console.log(`No pre-generated answer for question ${q.qNum}`);
      newMarkingResults[q.qNum] = `Correct: No\nFeedback: No model answer available for comparison.\nModel Answer: No model answer available.`;
    }
  }

  // Function to mark text input questions with AI
  async function markWithAI(q, userAnswer, newMarkingResults) {
    console.log(`=== AI MARKING DEBUG for Question ${q.qNum} ===`);
    console.log('Question type:', q.type);
    console.log('User answer:', userAnswer);
    console.log('Question text:', q.qText);
    
    const controller = new AbortController();
    const timeout = 60000;
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      let requestBody;
      const rawPassage = (ceptContent.readingPassage || passage || content || '').toString().trim();
      const passageForMarking = rawPassage.length <= 12000 ? rawPassage : `${rawPassage.slice(0, 9000)}\n...\n${rawPassage.slice(-2500)}`;
      
      if (q.type === 'sentence-completion') {
        requestBody = {
          passage: passageForMarking,
          question: q.qText,
          student_answer: userAnswer,
          max_marks: 1,
          question_type: 'sentence_completion',
          language: i18n.language || 'en',
          dialect: (i18n.language || '').toLowerCase().startsWith('zh') ? 'cantonese' : ''
        };
      } else {
        // For short answer questions
        requestBody = {
          passage: passageForMarking,
          question: q.qText,
          student_answer: userAnswer,
          max_marks: 1,
          question_type: 'short_answer',
          language: i18n.language || 'en',
          dialect: (i18n.language || '').toLowerCase().startsWith('zh') ? 'cantonese' : ''
        };
      }
      
      console.log(`Sending AI marking request for question ${q.qNum}:`, requestBody);
      
      const res = await fetch('/api/generate-marking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`Response status for question ${q.qNum}:`, res.status);
      console.log(`Response headers for question ${q.qNum}:`, res.headers);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`HTTP error for question ${q.qNum}:`, res.status, errorText);
        throw new Error(`HTTP ${res.status}: ${res.statusText} - ${errorText}`);
      }
      
      const data = await res.json();
      console.log(`AI marking response for question ${q.qNum}:`, data);
      
      if (data.success && data.marking_result) {
        console.log(`Successfully marked question ${q.qNum}:`, data.marking_result);
        newMarkingResults[q.qNum] = data.marking_result;
      } else {
        // Check if it's a rate limit error
        if (data.error && data.error.includes('429')) {
          console.log(`Rate limit hit for question ${q.qNum}, using fallback marking`);
          // Use fallback marking for rate limit errors
          newMarkingResults[q.qNum] = `Correct: No\nFeedback: AI service is temporarily unavailable. Please try again later.\nModel Answer: Unable to provide model answer at this time.`;
        } else {
          console.error(`API error for question ${q.qNum}:`, data.error);
          throw new Error(data.error || 'Invalid response from marking API');
        }
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error(`Exception during AI marking for question ${q.qNum}:`, err);
      
      // Check if it's a rate limit error
      if (err.message && err.message.includes('429')) {
        console.log(`Rate limit error for question ${q.qNum}, using fallback marking`);
        newMarkingResults[q.qNum] = `Correct: No\nFeedback: AI service is temporarily unavailable. Please try again later.\nModel Answer: Unable to provide model answer at this time.`;
      } else if (err.name === 'AbortError') {
        console.log(`Timeout error for question ${q.qNum}, using fallback marking`);
        newMarkingResults[q.qNum] = `Correct: No\nFeedback: AI marking timed out. Please try again.\nModel Answer: Unable to provide model answer at this time.`;
      } else {
        console.log(`Other error for question ${q.qNum}, using fallback marking`);
        newMarkingResults[q.qNum] = `Correct: No\nFeedback: Unable to mark this question at the moment. Please try again.\nModel Answer: No model answer available.`;
      }
    }
  }

  // Helper function to handle marking errors
  function handleMarkingError(error) {
    if (error.name === 'AbortError') {
      setRetryMessage('AI marking timed out. Please try again.');
    } else if (error.message.includes('429') || error.message.includes('rate-limited')) {
      if (retryCount < maxRetries) {
        const nextRetry = retryCount + 1;
        const delay = Math.pow(2, nextRetry) * 1000;
        
        setRetryCount(nextRetry);
        setRetryMessage(`AI service is busy. Retrying in ${delay/1000} seconds... (Attempt ${nextRetry}/${maxRetries})`);
        
        // Note: We can't retry here since submissionAnswers is not available in this scope
        // The retry logic should be handled in the calling function
        setRetryMessage('AI service is temporarily unavailable. Please try again in a few minutes.');
      } else {
        setRetryMessage('AI service is temporarily unavailable. Please try again in a few minutes.');
      }
    } else {
      setRetryMessage(`Marking failed: ${error.message}. Please try again.`);
    }
    
    setTimeout(() => {
      setIsMarking(false);
      setRetryMessage('');
    }, 3000);
  }

  function handleChange(qNum, value) {
    setUserAnswers(prev => ({ ...prev, [qNum]: value }));
  }

  // Function to handle input blur and reset viewport
  function handleInputBlur() {
    // Reset viewport scale on mobile devices when input loses focus
    if (window.innerWidth <= 768) {
      // Force viewport reset to zoom out
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        // Temporarily set a restrictive viewport to force zoom out
        viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
        
        // After a short delay, restore normal viewport settings
        setTimeout(() => {
          if (viewport) {
            viewport.setAttribute('content', 'width=device-width, initial-scale=1');
          }
        }, 100);
      }
      
      // Additional reset for iOS Safari with longer delay
      setTimeout(() => {
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1');
        }
      }, 300);
    }
  }

  // Function to render reading passage content
  function renderReadingPassage() {
    if (!content) return null;
    
    return (
      <div className="reading-passage-content">
        <h3>📖 Reading Passage</h3>
        <div className="passage-text">
          {ceptContent.readingPassage && (
            <div className="section">
              {/* Split the passage into paragraphs and render with proper spacing - match HKDSE approach */}
              {ceptContent.readingPassage.split('\n').map((line, index) => (
                line.trim() && (
                  <p key={index} style={{ marginBottom: '1em', lineHeight: '1.6' }}>
                    {line.trim()}
                  </p>
                )
              ))}
            </div>
          )}
        </div>
        
        {/* Question-Answer Book Toggle at the end of passage */}
        <div className="passage-toggle-container">
          <button
            className="mode-toggle-btn active"
            onClick={() => {
              setMode('practice');
              // Scroll to the content container with proper spacing above navbar
              setTimeout(() => {
                const contentContainer = document.querySelector('.cept-content');
                if (contentContainer) {
                  contentContainer.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                }
              }, 100);
            }}
          >
            <span className="page-emoji">📝</span>
            {t('labels.practiceMode', 'Question-Answer Book: Practice Mode')}
          </button>
        </div>
      </div>
    );
  }

  // Function to render practice mode
  function renderPracticeMode() {
    if (!questions || questions.length === 0) {
      return (
        <div className="practice-mode-form">
          <p>No questions available. Please generate new content.</p>
        </div>
      );
    }

    return (
      <div className="practice-mode-form">
        <h3>📝 Practice Questions</h3>
        
        {incompleteWarning && (
          <div className="warning">
            Please answer all questions before submitting.
          </div>
        )}
        
        <form onSubmit={handlePracticeSubmit}>
          {questions.map((question) => (
            <div key={question.qNum} className="question-block" ref={el => questionRefs.current[question.qNum] = el}>
              <h4>Question {question.qNum}</h4>
              <p>{question.qText}</p>
              
              {question.type === 'mcq' && question.options && (
                <div className="options-container">
                  {question.options.map((option) => (
                    <label key={option.label} className="option-label">
                      <input
                        type="radio"
                        name={`question-${question.qNum}`}
                        value={option.label}
                        checked={userAnswers[question.qNum] === option.label}
                        onChange={(e) => handleChange(question.qNum, e.target.value)}
                      />
                      <span className="option-text">{option.label}. {option.text}</span>
                    </label>
                  ))}
                </div>
              )}
              
              {question.type === 'short' && (
                <div className="short-answer">
                  <textarea
                    value={userAnswers[question.qNum] || ''}
                    onChange={(e) => handleChange(question.qNum, e.target.value)}
                    onBlur={handleInputBlur}
                    placeholder="Enter your answer here..."
                    rows="3"
                    className="answer-textarea"
                  />
                </div>
              )}

              {question.type === 'sentence-completion' && (
                <div className="sentence-completion">
                  {(() => {
                    // Parse the sentence to find blanks and create separate inputs
                    const sentence = question.qText;
                    const blankMatches = sentence.match(/_{3,}/g);
                    
                    if (blankMatches && blankMatches.length > 1) {
                      // Multiple blanks - create separate inputs
                      return (
                        <div className="multiple-blanks">
                          <p className="sentence-text">{sentence}</p>
                          <div className="blank-inputs">
                            {blankMatches.map((blank, index) => (
                              <div key={index} className="blank-input-group">
                                <label>Blank {index + 1}:</label>
                                <input
                                  type="text"
                                  value={userAnswers[`${question.qNum}_blank_${index}`] || ''}
                                  onChange={(e) => handleChange(`${question.qNum}_blank_${index}`, e.target.value)}
                                  onBlur={handleInputBlur}
                                  placeholder={`Enter word for blank ${index + 1}...`}
                                  className="completion-input"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    } else {
                      // Single blank - use original input
                      return (
                        <input
                          type="text"
                          value={userAnswers[question.qNum] || ''}
                          onChange={(e) => handleChange(question.qNum, e.target.value)}
                          onBlur={handleInputBlur}
                          placeholder="Enter the missing word..."
                          className="completion-input"
                        />
                      );
                    }
                  })()}
                </div>
              )}

              {/* Inline Results Display */}
              {showResults && markingResults[question.qNum] && (
                <div className="inline-result">
                  <div className="result-header">
                    {(() => {
                      const markingText = markingResults[question.qNum];
                      console.log(`Marking text for question ${question.qNum}:`, markingText);
                      
                      // Use HKDSE format: "Correct: Yes/No"
                      const correctMatch = markingText.match(/Correct:\s*(Yes|No)/i);
                      console.log('Correct match:', correctMatch);
                      
                      if (correctMatch) {
                        const isCorrect = correctMatch[1].toLowerCase() === 'yes';
                        return isCorrect ? (
                          <span className="result-correct">✅ Correct</span>
                        ) : (
                          <span className="result-incorrect">❌ Incorrect</span>
                        );
                      }
                      
                      // Fallback: Extract the model answer and compare with user answer
                      const modelAnswerMatch = markingText.match(/Model Answer:\s*(.*)/i);
                      const modelAnswer = modelAnswerMatch ? modelAnswerMatch[1].trim() : '';
                      
                      console.log('Model answer extracted:', modelAnswer);
                      
                      // For MCQ questions, extract the letter (A, B, C, D) from model answer
                      if (question.type === 'mcq') {
                        // Handle different formats: "B", "**B**", "**B (got)**", "B - explanation"
                        // Look for patterns like "**Correct answer: A**" or "Correct answer: A"
                        const correctAnswerMatch = modelAnswer.match(/(?:Correct answer:|Correct answer:)\s*\*\*?([ABCD])\*\*?/i);
                        if (correctAnswerMatch) {
                          const modelLetter = correctAnswerMatch[1].toUpperCase();
                          const userAnswer = userAnswers[question.qNum];
                          const isCorrect = modelLetter && userAnswer && modelLetter === userAnswer.toUpperCase();
                          
                          console.log('MCQ Debug:', {
                            modelAnswer,
                            modelLetter,
                            userAnswer,
                            isCorrect
                          });
                          
                          return isCorrect ? (
                            <span className="result-correct">✅ Correct</span>
                          ) : (
                            <span className="result-incorrect">❌ Incorrect</span>
                          );
                        }
                        
                        // Fallback: try to extract letter from the beginning
                        const modelLetterMatch = modelAnswer.match(/(?:^\*\*)?([ABCD])(?:\*\*)?(?:\s*\([^)]*\))?/i);
                        const modelLetter = modelLetterMatch ? modelLetterMatch[1].toUpperCase() : '';
                        const userAnswer = userAnswers[question.qNum];
                        const isCorrect = modelLetter && userAnswer && modelLetter === userAnswer.toUpperCase();
                        
                        console.log('MCQ Debug (fallback):', {
                          modelAnswer,
                          modelLetter,
                          userAnswer,
                          isCorrect
                        });
                        
                        return isCorrect ? (
                          <span className="result-correct">✅ Correct</span>
                        ) : (
                          <span className="result-incorrect">❌ Incorrect</span>
                        );
                      }
                      
                      // For sentence completion, check if user answer matches model answer
                      if (question.type === 'sentence-completion') {
                        const userAnswer = userAnswers[question.qNum]?.trim().toLowerCase();
                        
                        // For sentence completion, we rely entirely on the AI's assessment
                        // The AI will provide "Correct: Yes/No" based on grammar and context
                        // We don't need to compare with a pre-defined model answer
                        
                        // Just use the AI's assessment from the "Correct: Yes/No" field
                        const correctMatch = markingText.match(/Correct:\s*(Yes|No)/i);
                        if (correctMatch) {
                          const isCorrect = correctMatch[1].toLowerCase() === 'yes';
                          return isCorrect ? (
                            <span className="result-correct">✅ Correct</span>
                          ) : (
                            <span className="result-incorrect">❌ Incorrect</span>
                          );
                        }
                        
                        // If we can't parse the AI's assessment, default to incorrect
                        return <span className="result-incorrect">❌ Incorrect</span>;
                      }
                      
                      // For short answer questions
                      if (question.type === 'short') {
                        const userAnswer = userAnswers[question.qNum]?.trim().toLowerCase();
                        const modelAnswerText = modelAnswer.toLowerCase();
                        
                        // First, try keyword matching
                        const userWords = userAnswer.split(/\s+/);
                        const modelWords = modelAnswerText.split(/\s+/);
                        const commonWords = userWords.filter(word => modelWords.includes(word));
                        const keywordMatch = commonWords.length >= Math.min(userWords.length, modelWords.length) * 0.5;
                        
                        if (keywordMatch) {
                          return <span className="result-correct">✅ Correct</span>;
                        }
                        
                        // Default to incorrect if we can't determine
                        return <span className="result-incorrect">❌ Incorrect</span>;
                      }
                      
                      // Default fallback
                      return <span className="result-incorrect">❌ Incorrect</span>;
                    })()}
                  </div>
                  <div className="result-content">
                    <div><strong>Feedback:</strong> {markingResults[question.qNum].match(/Feedback:\s*(.*)/)?.[1] || 'No feedback available.'}</div>
                    <div><strong>Model Answer:</strong> {markingResults[question.qNum].match(/Model Answer:\s*(.*)/)?.[1] || 'No model answer available.'}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          <div className="submit-section">
            <button type="submit" className="submit-btn" disabled={isMarking}>
              {isMarking ? t('marking.inProgress', 'Marking...') : t('buttons.submit', 'Submit Answers')}
            </button>
            <p className="progress-text">
              {`Answered: ${answeredCount} / ${questions.length} questions`}
            </p>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cept-guide">
      {questionType === 'grammar' ? (
        // For grammar mode, show exercises directly without toggle
        <div className="exam-content">
          {renderPracticeMode()}
        </div>
      ) : (
        // For reading mode, show the toggle
        <>
          <div className="mode-toggle-container">
            <button
              className={`mode-toggle-btn${mode === 'reading' ? ' active' : ''}`}
              onClick={() => setMode('reading')}
            >
              <span className="page-emoji">📖</span>
              {t('labels.readingPassage', 'Reading Passages')}
            </button>
            <button
              className={`mode-toggle-btn${mode === 'practice' ? ' active' : ''}`}
              onClick={() => setMode('practice')}
            >
              <span className="page-emoji">📝</span>
              {t('labels.practiceMode', 'Question-Answer book: Practice Mode')}
            </button>
          </div>
          
          <div className="exam-content">
            {mode === 'reading' ? renderReadingPassage() : renderPracticeMode()}
          </div>
        </>
      )}

      {/* Marking Popup Modal */}
      {isMarking && (
        <div className="marking-overlay">
          <div className="marking-modal">
            <div className="marking-content">
              {!retryMessage ? (
                <>
                  <div className="marking-spinner">
                    <div className="spinner"></div>
                  </div>
                  <h3>{t('marking.title', '🤖 AI is marking your answers...')}</h3>
                  <p>{t('marking.description', 'Please wait while our AI analyzes your responses and provides detailed feedback.')}</p>
                  <div className="marking-progress">
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <p>{t('marking.analyzing', 'Analyzing grammar, comprehension, and providing personalized feedback...')}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="marking-spinner">
                    <div className="spinner"></div>
                  </div>
                  <h3>{t('marking.status', '🤖 AI Service Status')}</h3>
                  <p className="retry-message">{retryMessage}</p>
                  {retryCount > 0 && (
                    <div className="retry-info">
                      <p>{t('marking.attempt', 'Attempt {retryCount} of {maxRetries}', { retryCount, maxRetries })}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CEPTPage; 
